import { useCallback, useEffect, useRef, useState } from 'react';
import { messagingAPI } from '@/services/api';
import NativeWebRTCTransport from './NativeWebRTCTransport';

const terminal = new Set(['declined', 'cancelled', 'missed', 'failed', 'ended']);

export const useConversationCall = ({ socket, connected, conversation, sendCallEvent, onIncomingCall }) => {
  const transportRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const terminalTimerRef = useRef(null);
  const callRef = useRef(null);
  const sendCallEventRef = useRef(sendCallEvent);
  const [call, setCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connectionState, setConnectionState] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { callRef.current = call; }, [call]);
  useEffect(() => { sendCallEventRef.current = sendCallEvent; }, [sendCallEvent]);

  const cleanup = useCallback(() => {
    transportRef.current?.stop();
    transportRef.current = null;
    remoteStreamRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setConnectionState('idle');
    setMuted(false);
    setCameraOff(false);
  }, []);

  const showTerminalThenClear = useCallback((payload) => {
    if (terminalTimerRef.current) clearTimeout(terminalTimerRef.current);
    setCall((current) => current ? { ...current, ...payload } : null);
    cleanup();
    terminalTimerRef.current = setTimeout(() => setCall(null), 2500);
  }, [cleanup]);

  const makeTransport = useCallback(async (mediaType, callId) => {
    const config = await messagingAPI.getIceServers(callId);
    if (config.degraded) setError(config.warning);
    const transport = new NativeWebRTCTransport({
      iceServers: config.ice_servers || [],
      onIceCandidate: (candidate) => sendCallEvent('call_ice', { call_id: callId, candidate }).catch(() => {}),
      onTrack: (stream) => {
        remoteStreamRef.current = stream;
        setRemoteStream(stream);
      },
      onConnectionStateChange: setConnectionState,
    });
    const stream = await transport.initialize(mediaType);
    transportRef.current = transport;
    setLocalStream(stream);
    return transport;
  }, [sendCallEvent]);

  const startCall = useCallback(async (mediaType) => {
    if (!conversation || !connected) throw new Error('Reconnect before starting a call.');
    if (callRef.current && !terminal.has(callRef.current.status)) {
      throw new Error('Finish the current call before starting another.');
    }
    setError(null);
    const result = await sendCallEvent('call_start', {
      conversation_id: conversation.conversation_id,
      media_type: mediaType,
    });
    const nextCall = { ...result, direction: 'outgoing' };
    callRef.current = nextCall;
    setCall(nextCall);
    try {
      await makeTransport(mediaType, result.call_id);
      setConnectionState('ringing');
    } catch (cause) {
      await sendCallEvent('call_cancel', { call_id: result.call_id }).catch(() => {});
      setCall(null);
      cleanup();
      throw cause;
    }
  }, [cleanup, connected, conversation, makeTransport, sendCallEvent]);

  const acceptCall = useCallback(async () => {
    if (!call) return;
    try {
      setError(null);
      // Media must be ready before the caller is told to send its offer;
      // otherwise a fast offer can arrive before RTCPeerConnection exists.
      await makeTransport(call.media_type, call.call_id);
      const accepted = await sendCallEvent('call_accept', { call_id: call.call_id });
      setCall((current) => ({ ...current, ...accepted, status: 'accepted' }));
      setConnectionState('connecting');
    } catch (cause) {
      setError(cause.message || 'Unable to accept this call.');
      cleanup();
    }
  }, [call, cleanup, makeTransport, sendCallEvent]);

  const declineCall = useCallback(async () => {
    if (!call) return;
    await sendCallEvent('call_decline', { call_id: call.call_id }).catch(() => {});
    setCall(null);
    cleanup();
  }, [call, cleanup, sendCallEvent]);

  const endCall = useCallback(async () => {
    if (!call) return;
    const event = call.direction === 'outgoing' && call.status === 'ringing' ? 'call_cancel' : 'call_end';
    await sendCallEvent(event, { call_id: call.call_id }).catch(() => {});
    setCall(null);
    cleanup();
  }, [call, cleanup, sendCallEvent]);

  const toggleMute = useCallback(() => {
    setMuted(transportRef.current?.toggleMute() ?? false);
  }, []);
  const toggleCamera = useCallback(() => {
    setCameraOff(transportRef.current?.toggleCamera() ?? false);
  }, []);
  const switchCamera = useCallback(async () => transportRef.current?.switchCamera(), []);

  useEffect(() => {
    if (!socket) return undefined;
    const incoming = (payload) => {
      const activeCall = callRef.current;
      if (activeCall && !terminal.has(activeCall.status) && activeCall.call_id !== payload.call_id) {
        sendCallEventRef.current('call_decline', { call_id: payload.call_id }).catch(() => {});
        setError('Another call was declined while your current call is active.');
        return;
      }
      setError(null);
      onIncomingCall?.(payload);
      const nextCall = { ...payload, direction: 'incoming' };
      callRef.current = nextCall;
      setCall(nextCall);
      setConnectionState('ringing');
    };
    const accepted = async (payload) => {
      if (payload.call_id !== call?.call_id || call?.direction !== 'outgoing') return;
      setCall((current) => ({ ...current, ...payload, status: 'accepted' }));
      try {
        const offer = await transportRef.current?.createOffer();
        if (offer) await sendCallEvent('call_offer', { call_id: payload.call_id, description: offer });
      } catch (cause) {
        setError('Connection failed. Please try again.');
        endCall();
      }
    };
    const offer = async (payload) => {
      if (payload.call_id !== call?.call_id || call?.direction !== 'incoming') return;
      try {
        const answer = await transportRef.current?.applyOfferAndCreateAnswer(payload.description);
        if (answer) await sendCallEvent('call_answer', { call_id: payload.call_id, description: answer });
      } catch {
        setError('Connection failed. Please try again.');
      }
    };
    const answer = async (payload) => {
      if (payload.call_id === call?.call_id) await transportRef.current?.applyAnswer(payload.description);
    };
    const ice = async (payload) => {
      if (payload.call_id === call?.call_id) await transportRef.current?.addIceCandidate(payload.candidate);
    };
    const terminalEvent = (payload) => {
      if (payload.call_id === call?.call_id) {
        showTerminalThenClear(payload);
      }
    };
    socket.on('incoming_call', incoming);
    socket.on('call_accept', accepted);
    socket.on('call_offer', offer);
    socket.on('call_answer', answer);
    socket.on('call_ice', ice);
    ['call_decline', 'call_cancel', 'call_end'].forEach((event) => socket.on(event, terminalEvent));
    return () => {
      socket.off('incoming_call', incoming);
      socket.off('call_accept', accepted);
      socket.off('call_offer', offer);
      socket.off('call_answer', answer);
      socket.off('call_ice', ice);
      ['call_decline', 'call_cancel', 'call_end'].forEach((event) => socket.off(event, terminalEvent));
    };
  }, [call?.call_id, call?.direction, endCall, onIncomingCall, sendCallEvent, showTerminalThenClear, socket]);

  useEffect(() => {
    if (!call || call.status !== 'ringing') return undefined;
    const timer = setTimeout(() => {
      sendCallEvent('call_end', { call_id: call.call_id })
        .then((payload) => showTerminalThenClear(payload))
        .catch(() => showTerminalThenClear({ status: 'failed', terminal_reason: 'timeout' }));
    }, 45_000);
    return () => clearTimeout(timer);
  }, [call, sendCallEvent, showTerminalThenClear]);

  useEffect(() => () => {
    if (terminalTimerRef.current) clearTimeout(terminalTimerRef.current);
    const activeCall = callRef.current;
    if (activeCall && !terminal.has(activeCall.status)) {
      const event = activeCall.direction === 'outgoing' && activeCall.status === 'ringing'
        ? 'call_cancel'
        : 'call_end';
      sendCallEventRef.current(event, { call_id: activeCall.call_id }).catch(() => {});
    }
    cleanup();
  }, [cleanup]);

  return {
    call,
    localStream,
    remoteStream,
    connectionState,
    muted,
    cameraOff,
    error,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    toggleCamera,
    switchCamera,
    enabled: Boolean(conversation && connected && (!call || terminal.has(call.status))),
    isTerminal: terminal.has(call?.status),
  };
};

export default useConversationCall;
